(() => {
  if (window.GFHomeDiscoveryReady) return;
  window.GFHomeDiscoveryReady = true;
  // Separate the corrected event flow from older storefront tabs still running v1.
  const key = 'gf_product_interest_v2';
  const legacyKey = 'gf_product_interest_v1';
  const ttl = 30 * 24 * 60 * 60 * 1000;
  const priority = ['storage-beds', 'sofa', 'dining-table', 'mattress', 'shoe-rack', 'wardrobe', 'coffee-table', 'tv-console', 'dressing-table', 'bed-frames'];
  const handlePattern = /^[a-z0-9][a-z0-9-]{0,254}$/;
  const root = new URL(window.Shopify?.routes?.root || '/', location.origin);
  const editor = Boolean(window.Shopify?.designMode);
  const instances = new Map();
  let privacyPromise;
  let generation = 0;

  function privacyReady() {
    if (editor) return Promise.resolve();
    if (privacyPromise) return privacyPromise;
    privacyPromise = new Promise(resolve => {
      const deadline = Date.now() + 5000;
      let timer;
      const timeout = setTimeout(() => {clearTimeout(timer);resolve();}, 5500);
      const finish = () => {clearTimeout(timeout);clearTimeout(timer);resolve();};
      const load = () => {
        if (typeof window.Shopify?.customerPrivacy?.preferencesProcessingAllowed === 'function') return finish();
        if (typeof window.Shopify?.loadFeatures === 'function') {
          try {window.Shopify.loadFeatures([{name: 'consent-tracking-api', version: '0.2'}], finish);} catch {finish();}
          return;
        }
        if (Date.now() < deadline) timer = setTimeout(load, 100);
        else finish();
      };
      load();
    });
    return privacyPromise;
  }

  function allowed() {
    try {return !editor && window.Shopify?.customerPrivacy?.preferencesProcessingAllowed() === true;} catch {return false;}
  }

  function forget() {
    try {localStorage.removeItem(key);localStorage.removeItem(legacyKey);} catch {}
  }

  function readInterest() {
    if (!allowed()) return null;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || 'null');
      if (!saved || !/^\d+$/.test(String(saved.id)) || typeof saved.handle !== 'string' || !handlePattern.test(saved.handle) || !Number.isFinite(saved.at) || saved.at > Date.now() || Date.now() - saved.at > ttl) {forget();return null;}
      return {id: String(saved.id), handle: saved.handle, at: saved.at};
    } catch {forget();return null;}
  }

  function contextFrom(scope) {
    const element = scope.querySelector('[data-gf-product-context]');
    if (!element || !/^\d+$/.test(element.dataset.productId) || !handlePattern.test(element.dataset.productHandle || '')) return null;
    const groups = [...element.querySelectorAll('[data-gf-context-collection]')].filter(group => handlePattern.test(group.dataset.handle || ''));
    const collection = priority.map(handle => groups.find(group => group.dataset.handle === handle)).find(Boolean);
    return {id: element.dataset.productId, handle: element.dataset.productHandle, title: element.dataset.productTitle, collection: collection ? {handle: collection.dataset.handle, title: collection.dataset.title} : null};
  }

  function record() {
    if (!allowed()) return;
    const product = contextFrom(document);
    if (!product) return;
    try {localStorage.setItem(key, JSON.stringify({id: product.id, handle: product.handle, at: Date.now()}));} catch {}
  }

  function urlFor(path) {return new URL(path, root);}

  async function html(url, signal) {
    const response = await fetch(url, {signal, credentials: 'same-origin', headers: {'Accept': 'text/html'}});
    if (!response.ok) throw new Error('Unavailable');
    return new DOMParser().parseFromString(await response.text(), 'text/html');
  }

  function fallback(instance) {
    instance.controller?.abort();
    instance.controller = null;
    instance.signature = '';
    instance.section.querySelector('[data-gf-discovery-featured]').hidden = false;
    instance.section.querySelector('[data-gf-discovery-inspired]').hidden = true;
    instance.section.setAttribute('aria-labelledby', instance.featuredHeading);
    instance.section.removeAttribute('aria-busy');
    instance.section.querySelector('[data-gf-discovery-status]').textContent = '';
  }

  function candidates(scope, source, excluded) {
    const seen = new Set();
    return [...scope.querySelectorAll('[data-gf-recommendations] > [data-gf-candidate]')].filter(card => {
      const id = card.dataset.gfProductId;
      if (!/^\d+$/.test(id) || id === source.id || excluded.has(id) || seen.has(id)) return false;
      if (source.collection && !(card.dataset.gfCollectionHandles || '').includes('|' + source.collection.handle + '|')) return false;
      seen.add(id);
      return true;
    });
  }

  async function personalise(instance) {
    if (!instance.near || !instance.section.isConnected || editor) return;
    const interest = readInterest();
    if (!interest) {fallback(instance);instance.section.dataset.gfDiscoveryState = allowed() ? 'no-history' : 'preferences-not-allowed';return;}
    const signature = interest.id + ':' + interest.at + ':' + root.pathname;
    if (instance.signature === signature) return;
    fallback(instance);
    instance.signature = signature;
    const version = generation;
    const controller = new AbortController();
    instance.controller = controller;
    const timeout = setTimeout(() => controller.abort(), 6500);
    instance.section.setAttribute('aria-busy', 'true');
    instance.section.dataset.gfDiscoveryState = 'checking-source';
    const current = () => version === generation && allowed() && !controller.signal.aborted && instance.section.isConnected && instance.signature === signature;
    try {
      const sourceURL = urlFor('products/' + interest.handle);
      sourceURL.searchParams.set('section_id', 'gf-home-products');
      const sourceDocument = await html(sourceURL, controller.signal);
      const source = contextFrom(sourceDocument);
      if (!source || source.id !== interest.id) throw new Error('source-unavailable');
      const excluded = new Set((instance.section.dataset.gfCartIds || '').split(',').filter(Boolean));
      const recommendationsURL = urlFor('recommendations/products');
      recommendationsURL.searchParams.set('section_id', 'gf-home-products');
      recommendationsURL.searchParams.set('product_id', source.id);
      recommendationsURL.searchParams.set('limit', '10');
      recommendationsURL.searchParams.set('intent', 'related');
      let cards = [];
      instance.section.dataset.gfDiscoveryState = 'loading-related';
      try {cards = candidates(await html(recommendationsURL, controller.signal), source, excluded);} catch (error) {if (controller.signal.aborted) throw error;}
      if (cards.length < 4 && source.collection) {
        instance.section.dataset.gfDiscoveryState = 'loading-collection';
        const collectionURL = urlFor('collections/' + source.collection.handle);
        collectionURL.searchParams.set('section_id', 'gf-home-products');
        const alternatives = candidates(await html(collectionURL, controller.signal), source, excluded);
        const ids = new Set(cards.map(card => card.dataset.gfProductId));
        for (const card of alternatives) {if (!ids.has(card.dataset.gfProductId)) {cards.push(card);ids.add(card.dataset.gfProductId);}}
      }
      if (cards.length < 4) throw new Error('too-few-products');
      if (!current()) throw new Error('cancelled');
      const grid = instance.section.querySelector('[data-gf-inspired-grid]');
      grid.replaceChildren(...cards.slice(0, 4).map(card => document.importNode(card, true)));
      const sourceLink = instance.section.querySelector('[data-gf-source-link]');
      sourceLink.textContent = source.title;
      sourceLink.href = urlFor('products/' + source.handle).href;
      const browse = instance.section.querySelector('[data-gf-inspired-browse]');
      const browseLabel = instance.section.querySelector('[data-gf-browse-label]');
      if (source.collection) {
        browse.href = urlFor('collections/' + source.collection.handle).href;
        browseLabel.textContent = instance.section.dataset.gfExploreTemplate.replace('[collection]', source.collection.title);
      } else {browse.href = urlFor('collections/all').href;browseLabel.textContent = instance.defaultBrowseLabel;}
      if (!current()) return;
      instance.section.querySelector('[data-gf-discovery-featured]').hidden = true;
      instance.section.querySelector('[data-gf-discovery-inspired]').hidden = false;
      instance.section.setAttribute('aria-labelledby', instance.inspiredHeading);
      instance.section.querySelector('[data-gf-discovery-status]').textContent = instance.section.dataset.gfUpdateLabel;
      instance.section.dataset.gfDiscoveryState = 'inspired';
    } catch (error) {
      if (version === generation && instance.controller === controller) {fallback(instance);instance.section.dataset.gfDiscoveryState = error.message;}
    } finally {
      clearTimeout(timeout);
      if (instance.controller === controller) {instance.controller = null;instance.section.removeAttribute('aria-busy');}
    }
  }

  function setup(scope = document) {
    scope.querySelectorAll('[data-gf-discovery]').forEach(section => {
      if (instances.has(section)) return;
      const instance = {section, near: false, signature: '', controller: null, featuredHeading: section.querySelector('[data-gf-discovery-featured] h2').id, inspiredHeading: section.querySelector('[data-gf-discovery-inspired] h2').id, defaultBrowseLabel: section.querySelector('[data-gf-browse-label]').textContent};
      instances.set(section, instance);
      if (editor) return;
      if (!('IntersectionObserver' in window)) {instance.near = true;privacyReady().then(() => personalise(instance));return;}
      const observer = new IntersectionObserver(entries => {if (entries.some(entry => entry.isIntersecting)) {instance.near = true;observer.disconnect();privacyReady().then(() => personalise(instance));}}, {rootMargin: '250px'});
      instance.observer = observer;
      observer.observe(section);
    });
  }

  async function refresh({recordView = true} = {}) {
    if (editor) {instances.forEach(fallback);return;}
    await privacyReady();
    generation += 1;
    if (!allowed()) {
      if (recordView) forget();
      instances.forEach(instance => {fallback(instance);instance.section.dataset.gfDiscoveryState = 'preferences-not-allowed';});
      return;
    }
    if (recordView) record();
    instances.forEach(instance => {if (instance.controller) fallback(instance);personalise(instance);});
  }

  setup();
  privacyReady().then(refresh);
  document.addEventListener('visitorConsentCollected', refresh);
  document.addEventListener('shopify:section:load', event => {setup(event.target);});
  document.addEventListener('shopify:section:unload', event => {instances.forEach((instance, section) => {if (event.target.contains(section)) {instance.observer?.disconnect();fallback(instance);instances.delete(section);}});});
  window.addEventListener('pageshow', refresh);
  // Another tab's notification is not a new product view or a local consent decision.
  // In particular, never recreate removed history or replace a newer view with this tab's product.
  window.addEventListener('storage', event => {if (event.key === key || event.key === null) refresh({recordView: false});});
})();

